import React, { useCallback, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Config from "../../config/Config";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export default function ImageUpload({ type, projectId, currentUrl, onUpload, token, style }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const upload = useCallback(async (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Only JPEG, PNG, WebP, and GIF images are allowed.");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("File must be under 5MB.");
      return;
    }

    setUploading(true);
    try {
      // 1. Get presigned URL from Django
      const body = { type, content_type: file.type };
      if (type === "project-banner" && projectId) {
        body.project_id = projectId;
      }

      const presignRes = await axios.post(Config().presignUrl, body, {
        headers: { Authorization: `Token ${token}` },
      });

      const { presigned_url, public_url } = presignRes.data;

      // 2. Upload directly to S3
      await axios.put(presigned_url, file, {
        headers: { "Content-Type": file.type },
        // Don't send auth header to S3
        transformRequest: [(data, headers) => {
          delete headers.Authorization;
          delete headers.common?.Authorization;
          return data;
        }],
      });

      // 3. Notify parent
      onUpload(public_url);
      toast.success("Image uploaded");
    } catch (err) {
      console.error("Upload error:", err);
      const msg = err.response?.data?.error || "Upload failed.";
      toast.error(msg);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [type, projectId, token, onUpload]);

  const onFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  }, [upload]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  }, [upload]);

  const isAvatar = type === "avatar";

  return (
    <div
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      style={{
        position: "relative",
        width: isAvatar ? 80 : "100%",
        height: isAvatar ? 80 : 120,
        borderRadius: isAvatar ? "50%" : 8,
        border: dragOver ? "2px solid #59c6c1" : "2px dashed #334155",
        overflow: "hidden",
        cursor: uploading ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: currentUrl ? "transparent" : "#0f172a",
        transition: "border-color 150ms ease",
        flexShrink: 0,
        ...style,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={onFileSelect}
        style={{ display: "none" }}
      />

      {currentUrl && (
        <img
          src={currentUrl}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}

      {/* Overlay */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          color: currentUrl ? "rgba(255,255,255,0.85)" : "#64748b",
          fontSize: 11,
          textShadow: currentUrl ? "0 1px 3px rgba(0,0,0,0.6)" : "none",
          opacity: uploading ? 1 : (currentUrl ? 0 : 1),
          transition: "opacity 150ms ease",
          pointerEvents: "none",
        }}
        className="image-upload-overlay"
      >
        {uploading ? (
          <span style={{ fontSize: 12 }}>Uploading…</span>
        ) : (
          <>
            <CloudUploadIcon style={{ fontSize: isAvatar ? 20 : 24 }} />
            {!isAvatar && <span>Drop image or click</span>}
          </>
        )}
      </div>

      {/* Show overlay on hover when image exists */}
      <style>{`
        div:hover > .image-upload-overlay {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
}
